/**
 * Console Logger Utility
 * Captures console logs and writes them to a downloadable file
 * Only active when running on app-test.taloflow.ai
 */

interface LogEntry {
  type: 'log' | 'error' | 'warn' | 'info' | 'debug'
  timestamp: string
  message: string
  stack?: string
  data?: unknown
}

class ConsoleLogger {
  private logs: LogEntry[] = []
  private originalConsole: {
    log: typeof console.log
    error: typeof console.error
    warn: typeof console.warn
    info: typeof console.info
    debug: typeof console.debug
  }
  private isActive = false
  private maxLogs = 10000 // Maximum logs to keep in memory

  constructor() {
    this.originalConsole = {
      log: console.log.bind(console),
      error: console.error.bind(console),
      warn: console.warn.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
    }
  }

  private shouldActivate(): boolean {
    if (typeof window === 'undefined') return false
    return window.location.hostname === 'app-test.taloflow.ai'
  }

  private formatMessage(args: unknown[]): string {
    return args
      .map((arg) => {
        if (typeof arg === 'object' && arg !== null) {
          try {
            return JSON.stringify(arg, null, 2)
          } catch {
            return String(arg)
          }
        }
        return String(arg)
      })
      .join(' ')
  }

  private shouldSuppressWarning(message: string): boolean {
    // Suppress Y.js "Invalid access" warnings - these are expected during initialization
    // when valtio-yjs accesses maps before they're fully initialized
    if (message.includes('Invalid access: Add Yjs type to a document before reading data.')) {
      return true
    }
    return false
  }

  private captureLog(
    type: LogEntry['type'],
    args: unknown[],
    stack?: string
  ): void {
    if (!this.isActive) return

    const message = this.formatMessage(args)
    
    // Skip suppressed warnings
    if (type === 'warn' && this.shouldSuppressWarning(message)) {
      return
    }

    const entry: LogEntry = {
      type,
      timestamp: new Date().toISOString(),
      message,
      stack,
    }

    // Try to capture structured data if it's an object
    if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
      try {
        entry.data = JSON.parse(JSON.stringify(args[0]))
      } catch {
        // Ignore if can't serialize
      }
    }

    this.logs.push(entry)

    // Limit log size to prevent memory issues
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs)
    }
  }

  private setupInterceptors(): void {
    const methods: LogEntry['type'][] = ['log', 'error', 'warn', 'info', 'debug']

    methods.forEach((method) => {
      const original = this.originalConsole[method]
      // Type assertion needed to access console methods dynamically
      ;(console as Record<LogEntry['type'], typeof console.log>)[method] = (...args: unknown[]) => {
        const message = this.formatMessage(args)
        
        // Suppress Y.js "Invalid access" warnings from console output
        // These are expected during initialization when valtio-yjs accesses maps
        if (method === 'warn' && this.shouldSuppressWarning(message)) {
          // Don't call original console method or capture - completely suppress
          return
        }
        
        // Call original console method
        original(...args)

        // Capture the log
        let stack: string | undefined
        if (method === 'error') {
          try {
            throw new Error()
          } catch (e) {
            stack = (e as Error).stack
          }
        }
        this.captureLog(method, args, stack)
      }
    })

    // Also capture unhandled errors
    window.addEventListener('error', (event) => {
      this.captureLog('error', [
        `Unhandled Error: ${event.message}`,
        `File: ${event.filename}:${event.lineno}:${event.colno}`,
        event.error?.stack,
      ])
    })

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureLog('error', [
        `Unhandled Promise Rejection: ${event.reason}`,
        event.reason?.stack,
      ])
    })
  }

  public start(): void {
    if (!this.shouldActivate()) {
      return
    }

    if (this.isActive) {
      console.warn('Console logger is already active')
      return
    }

    this.isActive = true
    this.setupInterceptors()

    console.log('📝 Console logger activated for app-test.taloflow.ai')
    console.log('💾 Use window.saveConsoleLogs() to save logs to server')
    console.log('📥 Use window.downloadConsoleLogs() to download logs locally')
  }

  public stop(): void {
    this.isActive = false

    // Restore original console methods
    console.log = this.originalConsole.log
    console.error = this.originalConsole.error
    console.warn = this.originalConsole.warn
    console.info = this.originalConsole.info
    console.debug = this.originalConsole.debug
  }

  public getLogs(): LogEntry[] {
    return [...this.logs]
  }

  public clearLogs(): void {
    this.logs = []
    console.log('Console logs cleared')
  }

  public async saveLogsToServer(): Promise<void> {
    if (this.logs.length === 0) {
      console.warn('No logs to save')
      return
    }

    const logData = {
      hostname: window.location.hostname,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      logs: this.logs,
    }

    try {
      const response = await fetch('/api/console-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to save logs')
      }

      const result = await response.json()
      console.log(`✅ ${result.message}`)
      console.log(`📁 File saved: ${result.filename}`)
    } catch (error) {
      console.error('❌ Failed to save logs to server:', error)
      throw error
    }
  }

  public saveLogs(): void {
    if (this.logs.length === 0) {
      return
    }

    const logData = {
      hostname: window.location.hostname,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      logCount: this.logs.length,
      logs: this.logs,
    }

    const blob = new Blob([JSON.stringify(logData, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `console-logs-${new Date().toISOString().replace(/:/g, '-')}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    console.log(`💾 Saved ${this.logs.length} log entries to file`)
  }

  public getStats(): {
    total: number
    byType: Record<string, number>
  } {
    const byType: Record<string, number> = {}
    this.logs.forEach((log) => {
      byType[log.type] = (byType[log.type] || 0) + 1
    })

    return {
      total: this.logs.length,
      byType,
    }
  }
}

// Create singleton instance
const consoleLogger = new ConsoleLogger()

// Expose to window for manual control
if (typeof window !== 'undefined') {
  ;(window as unknown as Window & {
    saveConsoleLogs: () => Promise<void>
    downloadConsoleLogs: () => void
    clearConsoleLogs: () => void
    getConsoleLogStats: () => { total: number; byType: Record<string, number> }
  }).saveConsoleLogs = async () => {
    await consoleLogger.saveLogsToServer()
  }

  ;(window as unknown as Window & {
    downloadConsoleLogs: () => void
  }).downloadConsoleLogs = () => {
    consoleLogger.saveLogs()
  }

  ;(window as unknown as Window & {
    clearConsoleLogs: () => void
  }).clearConsoleLogs = () => {
    consoleLogger.clearLogs()
  }

  ;(window as unknown as Window & {
    getConsoleLogStats: () => { total: number; byType: Record<string, number> }
  }).getConsoleLogStats = () => {
    return consoleLogger.getStats()
  }
}

export { consoleLogger }

